import React, { useEffect, useRef, useState } from 'react';
import authedFetch from '../../lib/authedFetch';
import secureStorage from '../../lib/secureStorage';
import { computeChainLeak } from '../../lib/chainLeak';
import { OPPORTUNITY_LABELS } from '../../lib/solutionStages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reusable 4-pill strip showing the org's live LEI / PE / ROE / PE
// Validated values plus QoQ delta arrows. Pulls from
// `/api/health/quarter-deltas`. Drop it anywhere that needs a
// "what's the org's pulse right now" anchor.
//
// Props:
//   - organizationId?: string         — when set, scopes the fetch (Super Admin)
//   - roeContextFallback?: object     — optional fallback values when health snapshot has no history
//   - dataCounts?: { l2, motivation, l3 } — optional provenance line under the title
//   - title?: string                  — defaults to "Current Health Stats"
//   - layout?: 'card' | 'inline'      — `card` = padded card with title; `inline` = bare 4-pill row
//   - tone?: 'light' | 'dark'         — `light` = white pills (default), `dark` = frosted glass for dark backgrounds
//   - testId?: string
export const HealthStatsStrip = ({
  organizationId,
  roeContextFallback,
  dataCounts,
  title = 'Current Health Stats',
  layout = 'card',
  tone = 'light',
  className = '',
  testId = 'health-stats-strip',
  // Adds a "Highest Opportunity / Biggest Constraint" insights footer
  // beneath the pills (Navigation Conditions interpretation layer).
  showInsights = false,
  // Allow caller to inject a pre-fetched snapshot (e.g. from a parent
  // page that already loaded /quarter-deltas) to avoid redundant calls.
  prefetched = null,
}) => {
  const [snapshot, setSnapshot] = React.useState(prefetched);
  const [loading, setLoading] = React.useState(!prefetched);

  React.useEffect(() => {
    if (prefetched) {
      setSnapshot(prefetched);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const token = secureStorage.getItem('session_token');
      try {
        const orgParam = organizationId && organizationId !== 'all' ? `?organization_id=${organizationId}` : '';
        const res = await authedFetch(`${API_URL}/api/health/quarter-deltas${orgParam}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok && !cancelled) setSnapshot(await res.json());
      } catch { /* swallow — UI falls back to "—" */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [organizationId, prefetched]);

  const m = snapshot?.metrics || {};
  const lei = m.lei?.current ?? null;
  const pe = m.pe?.current ?? roeContextFallback?.pe_ideal_pct ?? null;
  const roe = m.roe?.current ?? roeContextFallback?.roe_pct ?? null;
  const peValid = m.kpi?.current ?? roeContextFallback?.pe_validated_pct ?? null;
  const fmt = (v) => v == null ? '—' : `${Number(v).toFixed(1)}%`;

  const renderDelta = (key) => {
    const d = m[key];
    if (!d || d.delta == null || d.direction == null) return null;
    if (d.direction === 'flat' || d.delta === 0) {
      return <span className={`${deltaSize} font-medium ${tone === 'dark' ? 'text-white/50' : 'text-slate-400'}`}>flat</span>;
    }
    const isUp = d.direction === 'up';
    const upCls = tone === 'dark' ? 'text-emerald-300' : 'text-emerald-600';
    const downCls = tone === 'dark' ? 'text-rose-300' : 'text-rose-600';
    return (
      <span className={`${deltaSize} font-bold tabular-nums ${isUp ? upCls : downCls}`}>
        {isUp ? '▲' : '▼'} {Math.abs(d.delta).toFixed(1)}%
      </span>
    );
  };

  // Tone-aware pill styling. `dark` uses frosted glass tiles + light
  // text so the strip integrates with navy/purple hero backgrounds
  // instead of fighting them with bright white cards.
  const pillBase = tone === 'dark'
    ? 'rounded-md bg-white/10 backdrop-blur-sm border p-2 text-center'
    : 'rounded-lg bg-white border p-3 text-center';
  const pillTextCls = (color) => {
    if (tone === 'dark') return 'text-white';
    return color;
  };
  const pillLabelCls = (color) => {
    if (tone === 'dark') return 'text-white/80';
    return color;
  };
  const pillBorder = (lightBorder) =>
    tone === 'dark' ? 'border-white/15' : lightBorder;

  // Sizing — bumped on the light tone so the metric label + value
  // + QoQ delta read at-a-glance from across the room. Dark tone
  // sizes are unchanged (already calibrated for the frosted hero).
  const labelSize = tone === 'dark' ? 'text-[10px]' : 'text-xs';
  const valueSize = tone === 'dark' ? 'text-base' : 'text-2xl';
  const deltaSize = tone === 'dark' ? 'text-xs' : 'text-sm';

  // Layout: dark-tone consumers (BCB hero) need a horizontally-
  // scrollable row below 640px so the 4 pills don't get crushed
  // on phones / split-screens. Light-tone keeps the simple grid
  // because its cards are already compact.
  const pillsLayoutCls = tone === 'dark'
    ? 'flex sm:grid sm:grid-cols-4 gap-2 overflow-x-auto sm:overflow-visible snap-x snap-mandatory -mx-1 px-1 sm:mx-0 sm:px-0'
    : 'grid grid-cols-4 gap-1.5';
  const pillFlexCls = tone === 'dark'
    ? 'flex-1 min-w-[44vw] sm:min-w-0 snap-start'
    : '';

  // Scroll-progress dots — only meaningful when the row scrolls
  // (tone="dark" + viewport below `sm`). We track scrollLeft and
  // compute the most-visible pill index. The dots are sm:hidden
  // so they vanish on the desktop grid.
  const scrollerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || tone !== 'dark') return;
    const onScroll = () => {
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) {
        setActiveIdx(0);
        return;
      }
      const ratio = el.scrollLeft / max;
      // 4 pills → indices 0..3
      setActiveIdx(Math.min(3, Math.max(0, Math.round(ratio * 3))));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [tone]);

  // Light tone gets a billboard layout: jumbo label on the left,
  // value + delta stacked tight on the right (matches the user's
  // mock — the metric ID should read across the room). Dark tone
  // keeps the centered vertical stack so it slots into the BCB
  // hero band without crowding.
  const isLight = tone !== 'dark';

  // Navigation Conditions — Missing Middle translation layer. Big
  // label = what the number means for value transfer; small hint =
  // the legacy metric code (LEI/PE/ROE/PE Valid) so the translation
  // is taught rather than hidden.
  const CONDITIONS = [
    { deltaKey: 'lei', hint: 'LEI',      label: 'Capability Readiness', border: 'border-blue-200',    hex: '#1d4ed8', value: lei },
    { deltaKey: 'pe',  hint: 'PE',       label: 'Adoption Readiness',   border: 'border-purple-200',  hex: '#7e22ce', value: pe },
    { deltaKey: 'roe', hint: 'ROE',      label: 'Value Conversion',     border: 'border-teal-200',    hex: '#0f766e', value: roe },
    { deltaKey: 'kpi', hint: 'PE Valid', label: 'Evidence Confidence',  border: 'border-fuchsia-200', hex: '#a21caf', value: peValid },
  ];
  const knownConditions = CONDITIONS.filter((c) => c.value != null);
  const lowestKey = knownConditions.length > 1
    ? knownConditions.reduce((a, b) => (b.value < a.value ? b : a)).deltaKey
    : null;

  const renderPill = (cond) => {
    const isLowest = cond.deltaKey === lowestKey;
    const lowestRing = isLowest
      ? (tone === 'dark' ? 'border-orange-300/60' : 'ring-1 ring-orange-300 border-orange-300')
      : '';
    if (isLight) {
      return (
        <div key={cond.deltaKey} className={`${pillBase} ${pillBorder(cond.border)} ${lowestRing} flex items-center justify-center gap-4 text-left`}>
          <div className="min-w-0">
            <div className="font-extrabold uppercase tracking-tight leading-tight text-base sm:text-lg" style={{ color: cond.hex }}>
              {cond.label}
            </div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              {cond.hint}
              {isLowest && (
                <span className="ml-1.5 text-orange-600" data-testid={`lowest-condition-${cond.deltaKey}`}>· Lowest Condition</span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-start gap-1 shrink-0">
            <div className="font-bold leading-none text-xl sm:text-2xl tabular-nums" style={{ color: cond.hex }}>
              {fmt(cond.value)}
            </div>
            <div className="leading-none">{renderDelta(cond.deltaKey)}</div>
          </div>
        </div>
      );
    }
    // Dark tone — unchanged vertical stack (+ metric-code hint line)
    return (
      <div key={cond.deltaKey} className={`${pillBase} ${pillBorder(cond.border)} ${lowestRing} ${pillFlexCls}`}>
        <div className={`${labelSize} font-bold uppercase tracking-wide ${pillLabelCls('text-white/80')}`}>{cond.label}</div>
        <div className="text-[9px] uppercase tracking-wider text-white/50">{cond.hint}</div>
        <div className={`${valueSize} font-bold leading-tight ${pillTextCls('text-white')}`}>{fmt(cond.value)}</div>
        <div className="mt-1">{renderDelta(cond.deltaKey)}</div>
      </div>
    );
  };

  const pills = (
    <>
      <div className={pillsLayoutCls} ref={scrollerRef}>
        {CONDITIONS.map(renderPill)}
      </div>
      {tone === 'dark' && (
        <div
          className="flex sm:hidden items-center justify-center gap-1.5 mt-2"
          aria-hidden
          data-testid="health-stats-scroll-dots"
        >
          {[0, 1, 2, 3].map(i => (
            <span
              key={i}
              className={`block h-1.5 rounded-full transition-all duration-200 ${
                i === activeIdx
                  ? 'w-4 bg-white/90'
                  : 'w-1.5 bg-white/35'
              }`}
            />
          ))}
        </div>
      )}
    </>
  );

  if (layout === 'inline') {
    return <div className={className} data-testid={testId}>{pills}</div>;
  }

  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-3 ${className}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-slate-900">{title}</span>
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-teal-100 text-teal-800">Live</span>
      </div>
      {dataCounts && (
        <div className="text-[11px] text-slate-500 mb-2">
          {dataCounts.l2 || 0} L2 · {dataCounts.motivation || 0} surveys · {dataCounts.l3 || 0} L3 obs.
        </div>
      )}
      {loading && !snapshot ? (
        <div className="text-xs text-slate-400 italic py-3 text-center">Loading…</div>
      ) : pills}
      {showInsights && !loading && (() => {
        const leak = computeChainLeak(m);
        const lowest = CONDITIONS.find((c) => c.deltaKey === lowestKey);
        if (!leak && !lowest) return null;
        return (
          <div className="flex flex-wrap gap-2 mt-2.5" data-testid="navigation-conditions-insights">
            {leak && (
              <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 border border-orange-200 px-2.5 py-1 text-[11px] font-semibold text-orange-700">
                Highest Opportunity: {OPPORTUNITY_LABELS[leak.key] || leak.stage.label}
              </span>
            )}
            {lowest && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                Biggest Constraint: {lowest.label}
              </span>
            )}
          </div>
        );
      })()}
      {snapshot?.has_history && (
        <div className="text-[9px] text-slate-400 mt-1.5 text-right">
          vs {snapshot.previous_quarter}
        </div>
      )}
    </div>
  );
};

export default HealthStatsStrip;
